import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { FileDown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { EstimateProvider, useEstimate } from '@/contexts/EstimateContext';
import EstimateItemTable from './EstimateItemTable';
import EstimateSummary from './EstimateSummary';
import ImageUploader from './ImageUploader';
import { generateEstimatePDF } from '@/utils/estimatePdf';
import { generateUUID } from '@/utils/uuid';
import { EstimateImage, saveImage } from '@/services/imageService';

const EstimateTabContent: React.FC = () => {
  const {
    estimateItems,
    clearEstimateItems,
    currentEstimateName,
    setCurrentEstimateName,
    currentEstimateId,
    setCurrentEstimateId,
    isEditing,
    customerInfo,
    updateCustomerInfo,
    settings,
    // Image management
    estimateImages,
    addEstimateImages,
    removeEstimateImage,
    updateEstimateImageDescription
  } = useEstimate();

  // Use the current estimate name from context
  const [estimateName, setEstimateName] = useState<string>('');

  // Update local state when currentEstimateName changes
  useEffect(() => {
    setEstimateName(currentEstimateName);
  }, [currentEstimateName]);

  // Toggle for showing/hiding project address fields
  const [showProjectAddress, setShowProjectAddress] = useState<boolean>(!customerInfo.useProjectAddress);

  // Initialize showProjectAddress based on customerInfo
  useEffect(() => {
    setShowProjectAddress(!customerInfo.useProjectAddress);
  }, [customerInfo.useProjectAddress]);

  // Handle toggling project address visibility
  const handleToggleProjectAddress = () => {
    const newValue = !showProjectAddress;
    setShowProjectAddress(newValue);
    updateCustomerInfo({ useProjectAddress: !newValue });
  };

  // Handle saving the estimate
  const handleSaveEstimate = async () => {
    if (!estimateName) {
      toast.error("Please enter a name for this estimate");
      return;
    }

    if (estimateItems.length === 0) {
      toast.error("Please add at least one item to the estimate");
      return;
    }

    // Show a loading toast for the entire save process
    const saveToast = toast.loading('Saving estimate...');

    try {
      // Get existing saved estimates from localStorage
      const savedEstimatesJSON = localStorage.getItem('savedEstimates') || '[]';
      const savedEstimates = JSON.parse(savedEstimatesJSON);

      // Use the settings from the context

      if (isEditing && currentEstimateId) {
        // Find the index of the estimate to update
        const estimateIndex = savedEstimates.findIndex((est: any) => est.id === currentEstimateId);

        if (estimateIndex !== -1) {
          // First, save all images to IndexedDB with the current estimate ID
          let savedImageReferences = [];

          if (estimateImages.length > 0) {
            try {
              console.log(`Processing ${estimateImages.length} images for estimate ${currentEstimateId}`);

              // Save each image to IndexedDB with the current estimate ID
              for (const img of estimateImages) {
                // Check if the image already has the correct estimateId
                if (img.estimateId !== currentEstimateId) {
                  // Extract the data we need
                  const { id, timestamp, estimateId, ...imageData } = img;

                  // Save the image with the current estimate ID
                  const savedImage = await saveImage(currentEstimateId, imageData);
                  console.log(`Saved image ${savedImage.id} for estimate ${currentEstimateId}`);

                  // Add to references
                  savedImageReferences.push({
                    id: savedImage.id,
                    fileName: savedImage.fileName,
                    description: savedImage.description,
                    timestamp: savedImage.timestamp
                  });
                } else {
                  // Image already has correct estimateId, just add to references
                  savedImageReferences.push({
                    id: img.id,
                    fileName: img.fileName,
                    description: img.description,
                    timestamp: img.timestamp
                  });
                }
              }
            } catch (error) {
              console.error('Error saving images to IndexedDB:', error);
              toast.error('Some images may not have been saved properly');
              // Continue with the save process even if some images failed
            }
          }

          // Update the existing estimate
          savedEstimates[estimateIndex] = {
            ...savedEstimates[estimateIndex],
            name: estimateName,
            date: new Date().toISOString(), // Update the date to now
            items: estimateItems,
            customerInfo: customerInfo,
            settings: settings, // Include the current settings
            // Store image references with the ones we just saved
            imageReferences: savedImageReferences.length > 0 ?
              savedImageReferences :
              estimateImages.map(img => ({
                id: img.id,
                fileName: img.fileName,
                description: img.description,
                timestamp: img.timestamp
              }))
          };

          // Save the updated array back to localStorage
          localStorage.setItem('savedEstimates', JSON.stringify(savedEstimates));

          // Dismiss loading toast and show success message
          toast.dismiss(saveToast);
          toast.success(`Estimate "${estimateName}" has been updated with ${savedImageReferences.length} images`);

          // Update the current estimate name in context
          setCurrentEstimateName(estimateName);
        } else {
          // If the estimate wasn't found (shouldn't happen), create a new one
          const newEstimate = {
            id: currentEstimateId,
            name: estimateName,
            date: new Date().toISOString(),
            items: estimateItems,
            customerInfo: customerInfo,
            settings: settings, // Include the current settings
          };

          savedEstimates.push(newEstimate);
          localStorage.setItem('savedEstimates', JSON.stringify(savedEstimates));

          toast.dismiss(saveToast);
          toast.success(`Estimate "${estimateName}" has been saved`);
        }
      } else {
        // Create a new estimate ID
        const newEstimateId = generateUUID();

        // Set the current estimate ID immediately so images are saved with the correct ID
        setCurrentEstimateId(newEstimateId);

        // First, save all images to IndexedDB with the new estimate ID
        let savedImageReferences = [];

        if (estimateImages.length > 0) {
          try {
            console.log(`Saving ${estimateImages.length} images for new estimate ${newEstimateId}`);

            // Save each image to IndexedDB with the new estimate ID
            for (const img of estimateImages) {
              // Extract the data we need, excluding id and timestamp which will be regenerated
              const { id, timestamp, estimateId, ...imageData } = img;

              // Save the image with the new estimate ID
              const savedImage = await saveImage(newEstimateId, imageData);
              console.log(`Saved image ${savedImage.id} for estimate ${newEstimateId}`);

              // Add to references
              savedImageReferences.push({
                id: savedImage.id,
                fileName: savedImage.fileName,
                description: savedImage.description,
                timestamp: savedImage.timestamp
              });
            }
          } catch (error) {
            console.error('Error saving images to IndexedDB:', error);
            toast.error('Some images may not have been saved properly');
            // Continue with the save process even if some images failed
          }
        }

        // Create a new estimate object
        const newEstimate = {
          id: newEstimateId,
          name: estimateName,
          date: new Date().toISOString(),
          items: estimateItems,
          customerInfo: customerInfo,
          settings: settings, // Include the current settings
          // Store image references that we just saved to IndexedDB
          imageReferences: savedImageReferences
        };

        // Add the new estimate to the array
        savedEstimates.push(newEstimate);

        // Save the updated array back to localStorage
        localStorage.setItem('savedEstimates', JSON.stringify(savedEstimates));

        // Dismiss loading toast and show success message
        toast.dismiss(saveToast);
        toast.success(`Estimate "${estimateName}" has been saved with ${savedImageReferences.length} images`);

        // Clear the form
        setEstimateName('');
        setCurrentEstimateName('');
      }
    } catch (error) {
      console.error('Error saving estimate:', error);
      toast.dismiss(saveToast);
      toast.error('Failed to save estimate. Please try again.');
    }
  };

  // Handle exporting the estimate as PDF
  const handleExportPDF = async () => {
    if (estimateItems.length === 0) {
      toast.error("Please add at least one item to the estimate");
      return;
    }

    // Show loading toast
    const pdfToast = toast.loading('Generating PDF...');

    try {
      // Use the settings from the context

      // Create estimate object
      const estimate = {
        name: estimateName || 'Glass Estimate',
        date: new Date().toISOString(),
        items: estimateItems,
        customerInfo: customerInfo,
        settings: settings // Include the current settings
      };

      // Make sure all images are properly saved before generating the PDF
      if (estimateImages.length > 0 && currentEstimateId) {
        console.log(`Ensuring ${estimateImages.length} images are properly saved before PDF generation`);

        // Check if any images need to be saved first
        const unsavedImages = estimateImages.filter(img => !img.estimateId || img.estimateId !== currentEstimateId);

        if (unsavedImages.length > 0) {
          console.log(`Saving ${unsavedImages.length} unsaved images before PDF generation`);

          // Save any unsaved images
          for (const img of unsavedImages) {
            const { id, timestamp, estimateId, ...imageData } = img;
            await saveImage(currentEstimateId, imageData);
          }
        }
      }

      // Generate PDF with images
      console.log(`Generating PDF with ${estimateImages.length} images`);
      const doc = generateEstimatePDF(estimate, estimateImages);

      // Save the PDF
      doc.save(`${estimate.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);

      // Dismiss loading toast and show success message
      toast.dismiss(pdfToast);
      toast.success("PDF has been generated and downloaded");
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.dismiss(pdfToast);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  // Handle clearing the estimate
  const handleClearEstimate = () => {
    if (estimateItems.length === 0) {
      toast.info("Estimate is already empty");
      return;
    }

    // Confirm before clearing
    if (window.confirm("Are you sure you want to clear all items from this estimate?")) {
      clearEstimateItems();
      toast.success("Estimate has been cleared");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">Glass Estimate</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClearEstimate}
            className="text-white border-[#4b5563] hover:bg-[#243447] hover:text-white"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear All
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPDF}
            className="text-white border-[#4b5563] hover:bg-[#243447] hover:text-white"
          >
            <FileDown className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      <Card className="bg-[#1e293b] text-white border-[#4b5563]">
        <CardHeader>
          <CardTitle className="text-[#60a5fa]">
            {isEditing ? 'Edit Estimate' : 'New Estimate'}
            {isEditing && <span className="text-sm font-normal text-muted-foreground ml-2">(editing existing estimate)</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Estimate Name */}
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-white mb-1">Estimate Name</label>
                <Input
                  type="text"
                  value={estimateName}
                  onChange={(e) => {
                    setEstimateName(e.target.value);
                    setCurrentEstimateName(e.target.value);
                  }}
                  className="bg-[#1e293b] text-white border-[#4b5563]"
                  placeholder="Enter estimate name"
                />
              </div>
              <div className="flex gap-2">
                {isEditing && (
                  <Button
                    onClick={clearEstimateItems}
                    variant="outline"
                    className="text-white border-[#4b5563] hover:bg-[#243447] hover:text-white"
                  >
                    Cancel Edit
                  </Button>
                )}
                <Button
                  onClick={handleSaveEstimate}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {isEditing ? 'Update Estimate' : 'Save Estimate'}
                </Button>
              </div>
            </div>

            {/* Company Information */}
            <div>
              <h3 className="text-md font-medium text-white mb-3">Company Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Company Name</label>
                  <Input
                    type="text"
                    value={customerInfo.companyName}
                    onChange={(e) => updateCustomerInfo({ companyName: e.target.value })}
                    className="bg-[#1e293b] text-white border-[#4b5563]"
                    placeholder="Enter company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Contact Name</label>
                  <Input
                    type="text"
                    value={customerInfo.contactName}
                    onChange={(e) => updateCustomerInfo({ contactName: e.target.value })}
                    className="bg-[#1e293b] text-white border-[#4b5563]"
                    placeholder="Enter contact name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Company Address</label>
                  <Input
                    type="text"
                    value={customerInfo.companyAddress}
                    onChange={(e) => updateCustomerInfo({ companyAddress: e.target.value })}
                    className="bg-[#1e293b] text-white border-[#4b5563]"
                    placeholder="Enter street address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Contact Phone</label>
                  <Input
                    type="text"
                    value={customerInfo.contactPhone}
                    onChange={(e) => updateCustomerInfo({ contactPhone: e.target.value })}
                    className="bg-[#1e293b] text-white border-[#4b5563]"
                    placeholder="Enter phone number"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">City</label>
                    <Input
                      type="text"
                      value={customerInfo.companyCity}
                      onChange={(e) => updateCustomerInfo({ companyCity: e.target.value })}
                      className="bg-[#1e293b] text-white border-[#4b5563]"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">State</label>
                    <Input
                      type="text"
                      value={customerInfo.companyState}
                      onChange={(e) => updateCustomerInfo({ companyState: e.target.value })}
                      className="bg-[#1e293b] text-white border-[#4b5563]"
                      placeholder="State"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Zip Code</label>
                    <Input
                      type="text"
                      value={customerInfo.companyZip}
                      onChange={(e) => updateCustomerInfo({ companyZip: e.target.value })}
                      className="bg-[#1e293b] text-white border-[#4b5563]"
                      placeholder="Zip Code"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Project Information */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-md font-medium text-white">Project Information</h3>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="sameAsCompany"
                    checked={!showProjectAddress}
                    onChange={handleToggleProjectAddress}
                    className="mr-2"
                  />
                  <label htmlFor="sameAsCompany" className="text-sm text-white cursor-pointer">
                    Same as company address
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-1">Project Name</label>
                  <Input
                    type="text"
                    value={customerInfo.projectName}
                    onChange={(e) => updateCustomerInfo({ projectName: e.target.value })}
                    className="bg-[#1e293b] text-white border-[#4b5563]"
                    placeholder="Enter project name"
                  />
                </div>
              </div>

              {showProjectAddress && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-1">Project Address</label>
                    <Input
                      type="text"
                      value={customerInfo.projectAddress}
                      onChange={(e) => updateCustomerInfo({ projectAddress: e.target.value })}
                      className="bg-[#1e293b] text-white border-[#4b5563]"
                      placeholder="Enter project address"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">City</label>
                      <Input
                        type="text"
                        value={customerInfo.projectCity}
                        onChange={(e) => updateCustomerInfo({ projectCity: e.target.value })}
                        className="bg-[#1e293b] text-white border-[#4b5563]"
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">State</label>
                      <Input
                        type="text"
                        value={customerInfo.projectState}
                        onChange={(e) => updateCustomerInfo({ projectState: e.target.value })}
                        className="bg-[#1e293b] text-white border-[#4b5563]"
                        placeholder="State"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-1">Zip Code</label>
                      <Input
                        type="text"
                        value={customerInfo.projectZip}
                        onChange={(e) => updateCustomerInfo({ projectZip: e.target.value })}
                        className="bg-[#1e293b] text-white border-[#4b5563]"
                        placeholder="Zip Code"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#1e293b] text-white border-[#4b5563]">
        <CardHeader>
          <CardTitle className="text-[#60a5fa]">Estimate Items</CardTitle>
        </CardHeader>
        <CardContent>
          <EstimateItemTable />
        </CardContent>
      </Card>

      <EstimateSummary />

      <Card className="bg-[#1e293b] text-white border-[#4b5563]">
        <CardHeader>
          <CardTitle className="text-[#60a5fa]">Attached Images</CardTitle>
        </CardHeader>
        <CardContent>
          <ImageUploader
            images={estimateImages}
            onAddImages={addEstimateImages}
            onRemoveImage={removeEstimateImage}
            onUpdateImageDescription={updateEstimateImageDescription}
          />
        </CardContent>
      </Card>
    </div>
  );
};

const EstimateTab: React.FC = () => {
  return <EstimateTabContent />;
};

export default EstimateTab;
